import { Feature, Polygon, FeatureCollection, MultiPolygon } from 'geojson';
import { Field } from './field';
import { Crop } from './crop';
import {
  CommonCommodity,
  CommonIntendedUse,
  CommonQuality,
  CommonUse,
  CommonVariety,
} from './commonService';
import { Commodity } from './commodity';
import { Variety } from './variety';

export class HubfarmSyncStatus {
  syncedResources: HubfarmSyncItem[] = [];
  addedResources: HubfarmSyncItem[] = [];
  removedResources: HubfarmSyncItem[] = [];
  updatedResources: HubfarmSyncItem[] = [];
}

export class HubfarmSyncRequest {
  resourcesToAdd: HubfarmSyncItem[] = [];
  resourcesToRemove: HubfarmSyncItem[] = [];
}

export class HubfarmSyncItem {
  multiFarmParent: boolean = false;
  cuaa: number = null;
  denominazione: string = null;
  userRoles: string[] = [];
  preferences: HubfarmSyncStatusPreference[] = [];
  metaData: HubfarmSyncItemMetadata[] = [];
  subRole: string = null;
}

export class HubfarmSyncItemMetadata {
  key: string = null;
  value: string = null;
  unit: string = null;
}

export class HubfarmSyncStatusPreference {
  key: string = null;
  value: string = null;
  priority: number = null;
  // Possible values: "String", "Number", "Boolean"
  type: string = null;
}

/**
 * Interface representing a single cultivation entry.
 */
export interface HubfarmCultivation {
  /** Cultivation start date in "dd/MM/yyyy" format */
  dataInizColt?: string;
  /** Quality code (from the products matrix) */
  codiQual?: string;
  /** Unique cultivation identifier */
  idColt?: string;
  /** Zootecnic effluents (as per the decodings) */
  effluentiZootecnici?: string;
  /** Irrigation potential (as per the decodings) */
  potenzialitaIrrigua?: string;
  /** Company structures (as per the decodings) */
  struttureAziendali?: string;
  /** Nominal area of the cultivation (should not exceed the plot area) */
  supeColt?: number;
  /** Flag indicating organic cultivation presence ("SI", "NO", or empty) */
  flagBiol?: string;
  /** Occupation variety code (from the products matrix) */
  codiOccuVari?: string;
  /** Occupation code (from the products matrix) */
  codiOccu?: string;
  /** Usage code (from the products matrix) */
  codiUsoo?: string;
  /** Crop rotation (from the decodings) */
  rotazioneColturale?: string;
  /** Destination use code (from the products matrix) */
  codiDestUsoo?: string;
  /** Flag for primary cultivation ("SI", "NO", or empty) */
  flagColtPrin?: string;
  /** Unique identifier of the previous cultivation (if applicable) */
  idColtLast?: string;
  /** Maintenance criteria (as per the decodings) */
  critMant?: string;
  /** Type of sowing (as per the decodings) */
  tipoDiSemina?: string;
  /** Cultivation phase (as per the decodings) */
  faseDiAllevamento?: string;
  /** Cultivation form (as per the decodings) */
  formaDiAllevamento?: string;
  /** Flag for DOP presence ("SI", "NO", or empty) */
  flagDop?: string;
  /** Indicator for terrace presence (as per the decodings) */
  presenzaTerrazzamenti?: string;
  /** Irrigation type (as per the decodings) */
  tipoIrrigazione?: string;
  /** Dangerous substances indicator (as per the decodings) */
  sostanzePericolose?: string;
  /** Type of cultivation (e.g., for vines; as per the decodings) */
  tipoDiAllevamento?: string;
  /** Flag for integrated production ("SI", "NO", or empty) */
  flagProdInte?: string;
  /** Indicator for permanent meadows (as per the decodings) */
  pratPerm?: string;
  /** Last year of cutting (if applicable) */
  ultimoAnnoDiTaglio?: string;
  /** Productive destinations (as per the decodings) */
  destinazioniProduttive?: string;
  /** Cultivation end date in "dd/MM/yyyy" format */
  dataFineColt?: string;
  /** Macro use code (from the products matrix) */
  codiDestUtil?: string;
  /** Planting year and month */
  annoMese?: string;
  /** Unique identifier at the CUAA level and PCG consolidation date */
  identificativoPianoColtivazione?: string;
  /** CUAA of the subject (must exist in the registry) */
  Cuaa?: string;
  /** Plot end date in "dd/MM/yyyy" format */
  dataFineAppe?: string;
  /** Previous validation sheet barcode */
  codiBarrScheValiOld?: string;
  /** Previous validation sheet printing date */
  dataStamScheValiOld?: string;
  /** Current validation sheet printing date */
  dataStampScheVali?: string;
  /** Identifier of the previous plot */
  idAppeLast?: string;
}

/**
 * Interface representing the properties of a feature.
 */
export interface HubfarmProperties {
  /** Number of points in the geometry */
  numPoints?: number;
  /** Code detected as declared by the producer (must be one of the SIPA codes) */
  codiceRilevato?: string;
  /** EPSG code reference */
  codiceEpsg?: string;
  /** Plot start date in "dd/MM/yyyy" format */
  dataInizAppe: string;
  /** Plot end date in "dd/MM/yyyy" format */
  dataFineAppe?: string;
  /** Version of the cultivation plan (PCG) */
  idPcgVers?: string;
  /** Barcode of the validation sheet */
  codiBarrScheVali?: string;
  /** Product code detected as declared by the producer (from SIPA codes) */
  codiceProdottoRilevato?: string;
  /** Area? of the polygon */
  areaPoligono?: number;
  /** Identifier of the validation sheet */
  idSchedaValidazione?: string;
  /** Unique identifier */
  id?: string;
  /** Alphanumeric table index */
  indiceTabella?: string;
  /** N?ominal area of the plot */
  superficieAppezzamento?: number;
  /** Identifier of the island */
  identificativoIsola?: string;
  /** Geometry type as a string (e.g., "POLYGON" or "MULTIPOLYGON") */
  tipo?: string;
  /** Identifier of the plot */
  identificativoAppezzamento?: string;
  /** Name of the layer */
  nomeLayer?: string;
  /** Feature label used in the graphical dossier */
  featureLabel?: string;
  /** Array of cultivation entries associated with the plot */
  coltivazioni?: HubfarmCultivation[];
  /** some date boohhh in "dd/MM/yyyy" format */
  dataStamScheVali?: string;
  identificativoPianoColtivazione?: string;
  /** cuaa **/
  cuaa?: string;
  // used only by FE to easily look for groupable items
  groupId?: string;
  // ids generated for front end list track by
  clientUniqueId?: number;
}

export type HubfarmFeature = Feature<Polygon | MultiPolygon, HubfarmProperties>;
export type HubfarmFeatureCollection = FeatureCollection<Polygon | MultiPolygon, HubfarmProperties>;
export type HubfarmFeatureGroup = {
  id: string;
  collapsed: boolean;
  features: HubfarmFeature[];
};

export type HubfarmMatchFieldFeatureGroup = {
  id: string;
  collapsed: boolean;
  features: HubfarmMatchFieldFeature[];
};

export interface HubfarmMatchFieldFeature {
  field: Field;
  feature: HubfarmFeature;
}

export interface HubfarmMatchFieldCrops {
  // locally generated just for selecting and tracking in angular
  parentId: string;
  id: string | number;
  field: Field;
  crop: Crop;
}

export interface MatchedHubfarmCultivationData {
  commonCommodity?: CommonCommodity;
  commodity?: Commodity;
  commonVariety?: CommonVariety;
  variety?: Variety;
  commonIntendedUse?: CommonIntendedUse;
  commonQuality?: CommonQuality;
  commonUse?: CommonUse;
  cultivation?: HubfarmCultivation;
  translatedName?: string;
}

export const DOSSIER_COLUMN_CONFIG = [
  {
    id: 'tipoAzienda',
    itemName: 'tipoAzienda',
    dataType: 'string',
  },
  {
    id: 'denominazione',
    itemName: 'denominazione',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.denominazione',
  },
  {
    id: 'nomePersonaFisica',
    itemName: 'nomePersonaFisica',
    dataType: 'string',
  },
  {
    id: 'comuneNascitaPersonaFisica',
    itemName: 'comuneNascitaPersonaFisica',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.comuneNascitaPersonaFisica',
  },
  {
    id: 'dataNascitaPersonaFisica',
    itemName: 'dataNascitaPersonaFisica',
    dataType: 'dateUTC',
  },
  {
    id: 'numeroDocumento',
    itemName: 'numeroDocumento',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.numeroDocumento',
  },
  {
    id: 'dataDocumento',
    itemName: 'dataDocumento',
    dataType: 'dateUTC',
    infopoint: 'dossier_table_infopoint.dataDocumento',
  },
  {
    id: 'dataScadenzaDocumento',
    itemName: 'dataScadenzaDocumento',
    dataType: 'dateUTC',
    infopoint: 'dossier_table_infopoint.dataScadenzaDocumento',
  },
  {
    id: 'sedeResidenzaProvincia',
    itemName: 'sedeResidenzaProvincia',
    dataType: 'string',
  },
  {
    id: 'sedeResidenzaComune',
    itemName: 'sedeResidenzaComune',
    dataType: 'string',
  },
  {
    id: 'sedeResidenzaIndirizzo',
    itemName: 'sedeResidenzaIndirizzo',
    dataType: 'string',
  },
  {
    id: 'sedeResidenzaCap',
    itemName: 'sedeResidenzaCap',
    dataType: 'string',
  },
  {
    id: 'sedeResidenzaEstero',
    itemName: 'sedeResidenzaEstero',
    dataType: 'string',
  },
  {
    id: 'recapitoProvincia',
    itemName: 'recapitoProvincia',
    dataType: 'string',
  },
  {
    id: 'recapitoComune',
    itemName: 'recapitoComune',
    dataType: 'string',
  },
  {
    id: 'recapitoIndirizzo',
    itemName: 'recapitoIndirizzo',
    dataType: 'string',
  },
  {
    id: 'recapitoCap',
    itemName: 'recapitoCap',
    dataType: 'string',
  },
  {
    id: 'recapitoEstero',
    itemName: 'recapitoEstero',
    dataType: 'string',
  },
  {
    id: 'numeroIscrizioneRea',
    itemName: 'numeroIscrizioneRea',
    dataType: 'string',
  },
  {
    id: 'dataInizioRea',
    itemName: 'dataInizioRea',
    dataType: 'dateUTC',
  },
  {
    id: 'dataFineRea',
    itemName: 'dataFineRea',
    dataType: 'dateUTC',
  },
  {
    id: 'orgaPaga',
    itemName: 'orgaPaga',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.orgaPaga',
  },
  {
    id: 'dataApertura',
    itemName: 'dataApertura',
    dataType: 'dateUTC',
  },
  {
    id: 'dataChiusura',
    itemName: 'dataChiusura',
    dataType: 'dateUTC',
  },
  {
    id: 'tipoDetentore',
    itemName: 'tipoDetentore',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.tipoDetentore',
  },
  {
    id: 'detentore',
    itemName: 'detentore',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.detentore',
  },
  {
    id: 'dataValidita',
    itemName: 'dataValidita',
    dataType: 'dateUTC',
  },
  {
    id: 'schedaValidita',
    itemName: 'schedaValidita',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.schevali',
  },
  {
    id: 'dataSchedaValidita',
    itemName: 'dataSchedaValidita',
    dataType: 'dateUTC',
  },
  {
    id: 'dataMandato',
    itemName: 'dataMandato',
    dataType: 'dateUTC',
  },
  {
    id: 'datafinevali',
    itemName: 'datafinevali',
    dataType: 'dateUTC',
  },
  {
    id: 'datainizvalid',
    itemName: 'datainizvalid',
    dataType: 'dateUTC',
  },
  {
    id: 'sessoPersonaFisica',
    itemName: 'sessoPersonaFisica',
    dataType: 'string',
  },
  {
    id: 'tipoDocumento',
    itemName: 'tipoDocumento',
    dataType: 'flag',
    flagConfig: {
      '1': 'Carta di identità',
      '2': 'Patente auto',
      '3': 'Tessera Ferrovie dello Stato',
      '4': 'Passaporto',
      '5': 'Tessera portale',
      '6': 'Altro documento',
      '7': 'Libretto postale',
    },
  },
  {
    id: 'descpec',
    itemName: 'descpec',
    dataType: 'string',
  },
  {
    id: 'email',
    itemName: 'email',
    dataType: 'string',
  },
  {
    id: 'numeroIscrizioneRi',
    itemName: 'numeroIscrizioneRi',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.numeroIscrizioneRi',
  },
  {
    id: 'dataInizioRi',
    itemName: 'dataInizioRi',
    dataType: 'dateUTC',
  },
  {
    id: 'dataFineRi',
    itemName: 'dataFineRi',
    dataType: 'dateUTC',
  },
  {
    id: 'numeroIscrizioneINPS',
    itemName: 'numeroIscrizioneINPS',
    dataType: 'string',
  },
  {
    id: 'codiceTipoINPS',
    itemName: 'codiceTipoINPS',
    dataType: 'flag',
    flagConfig: {
      '1': 'Coltivatore diretto',
      '2': 'Imprenditore agricolo professionale',
      '3': 'Colono',
      '4': 'Mezzadro',
      '5': 'Altro',
    },
  },
  {
    id: 'dataInizioINPS',
    itemName: 'dataInizioINPS',
    dataType: 'dateUTC',
  },
  {
    id: 'dataFineINPS',
    itemName: 'dataFineINPS',
    dataType: 'dateUTC',
  },
];

export const EQUIPMENT_COLUMN_CONFIG = [
  {
    id: 'targa',
    itemName: 'targa',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.targa',
  },
  {
    id: 'tipoTarga',
    itemName: 'tipoTarga',
    dataType: 'flag',
    flagConfig: {
      F: 'Senza targa',
      S: 'Stradale',
      R: 'Stradale rimorchi',
      T: 'Triangolare',
    },
  },
  {
    id: 'marca',
    itemName: 'marca',
    dataType: 'string',
  },
  {
    id: 'modello',
    itemName: 'modello',
    dataType: 'string',
  },
  {
    id: 'telaio',
    itemName: 'telaio',
    dataType: 'string',
  },
  {
    id: 'carburante',
    itemName: 'carburante',
    dataType: 'flag',
    flagConfig: {
      B: 'Benzina',
      G: 'Gasolio',
      P: 'Petrolio',
      N: 'No carburante',
    },
  },
  {
    id: 'trazione',
    itemName: 'trazione',
    dataType: 'flag',
    flagConfig: {
      C: 'Cingoli',
      DT: 'Doppia trazione',
      F: 'Fisso',
      R: 'Route',
      SC: 'Semicingoli',
    },
  },
  {
    id: 'tipoMacchina',
    itemName: 'tipoMacchina',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.tipoMacchina',
  },
  {
    id: 'formaPosseso',
    itemName: 'formaPosseso',
    dataType: 'flag',
    flagConfig: {
      L: 'Leasing',
      N: 'A nolo',
      P: 'Proprietario',
      PU: 'Proprietario utilizzatore',
      U: 'Utilizzatore',
      A: 'Non riconosciuto',
      M: 'Non riconosciuto',
    },
  },
  {
    id: 'dataIscrizione',
    itemName: 'dataIscrizione',
    dataType: 'dateUTC',
  },
  {
    id: 'dataCessazione',
    itemName: 'dataCessazione',
    dataType: 'dateUTC',
  },
  {
    id: 'datainizvalid',
    itemName: 'datainizvalid',
    dataType: 'dateUTC',
  },
  {
    id: 'datafinevali',
    itemName: 'datafinevali',
    dataType: 'dateUTC',
  },
];

export const FABBRICATO_COLUMN_CONFIG = [
  {
    id: 'comune',
    itemName: 'comune',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.comune',
  },
  {
    id: 'provincia',
    itemName: 'provincia',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.provincia',
  },
  {
    id: 'sezione',
    itemName: 'sezione',
    dataType: 'string',
  },
  {
    id: 'foglio',
    itemName: 'foglio',
    dataType: 'string',
  },
  {
    id: 'particella',
    itemName: 'particella',
    dataType: 'string',
  },
  {
    id: 'subalterno',
    itemName: 'subalterno',
    dataType: 'string',
  },
  {
    id: 'tipoConduzione',
    itemName: 'tipoConduzione',
    dataType: 'flag',
    flagConfig: {
      '1': 'Proprietà o comproprietà',
      '2': 'Affitto',
      '3': 'Mezzadria',
      '4': 'Altra forma',
      '5': 'Est.Inf.5.000mq Comune Montano(DL.24/06/2014 N.91)',
    },
  },
  {
    id: 'tipoConduzione',
    itemName: 'tipoConduzione',
    dataType: 'flag',
    flagConfig: {
      '1': 'Proprietà o comproprietà',
      '2': 'Affitto',
      '3': 'Mezzadria',
      '4': 'Altra forma',
      '5': 'Est.Inf.5.000mq Comune Montano(DL.24/06/2014 N.91)',
    },
  },
  {
    id: 'dataInizioConduzione',
    itemName: 'dataInizioConduzione',
    dataType: 'dateUTC',
  },
  {
    id: 'dataFineConduzione',
    itemName: 'dataFineConduzione',
    dataType: 'dateUTC',
  },
  {
    id: 'dataInizioConduzione',
    itemName: 'dataInizioConduzione',
    dataType: 'dateUTC',
  },
  {
    id: 'dataFineConduzione',
    itemName: 'dataFineConduzione',
    dataType: 'dateUTC',
  },
  {
    id: 'superficieCatastale',
    itemName: 'superficieCatastale',
    dataType: 'number',
    infopoint: 'dossier_table_infopoint.superficieCatastale',
  },
  {
    id: 'superficieCondotta',
    itemName: 'superficieCondotta',
    dataType: 'number',
    infopoint: 'dossier_table_infopoint.superficieCondotta',
  },
  {
    id: 'superficieGrafica',
    itemName: 'superficieGrafica',
    dataType: 'number',
  },
  {
    id: 'superficieCoperta',
    itemName: 'superficieCoperta',
    dataType: 'number',
  },
  {
    id: 'superficieScoperta',
    itemName: 'superficieScoperta',
    dataType: 'number',
  },
  {
    id: 'numeroPosti',
    itemName: 'numeroPosti',
    dataType: 'number',
  },
  {
    id: 'occupazioneSuolo',
    itemName: 'occupazioneSuolo',
    dataType: 'string',
  },
  {
    id: 'destinazioneUso',
    itemName: 'destinazioneUso',
    dataType: 'string',
  },
  {
    id: 'qualita',
    itemName: 'qualita',
    dataType: 'string',
  },
  {
    id: 'flagirrigua',
    itemName: 'flagirrigua',
    dataType: 'flag',
    flagConfig: {
      '0': 'Particella non irrigua',
      '1': 'Particella irrigua',
      '2': 'Non dichiarato',
    },
  },
  {
    id: 'flagterrazzata',
    itemName: 'flagterrazzata',
    dataType: 'flag',
    flagConfig: {
      '0': 'Particella senza terrazzamenti o livellamenti',
      '1': 'Particella con terrazzamenti',
      '2': 'Particella con livellamenti',
      '3': 'Particella con terrazzamenti e livellamenti',
    },
  },
  {
    id: 'rotazionecoltureortive',
    itemName: 'rotazionecoltureortive',
    dataType: 'flag',
    flagConfig: {
      '0': 'Particella senza rotazione colturale',
      '1': 'Particella con ciclo ortivo',
      '2': 'Particella con ciclo seminativo',
      '3': 'Non dichiarato',
    },
  },
  {
    id: 'effluentizootecnici',
    itemName: 'effluentizootecnici',
    dataType: 'flag',
    flagConfig: {
      '0': 'Particella senza effluenti zootecnici',
      '1': 'Particella con effluenti zootecnici',
    },
  },
  {
    id: 'sostanzepericolose',
    itemName: 'sostanzepericolose',
    dataType: 'flag',
    flagConfig: {
      '0': 'Particella senza sostanze pericolose',
      '1': 'Particella con sostanze pericolose',
    },
  },
  {
    id: 'casiparticolari',
    itemName: 'casiparticolari',
    dataType: 'string',
  },
  {
    id: 'flaggiust',
    itemName: 'flaggiust',
    dataType: 'flag',
    flagConfig: {
      '0': 'No',
      '1': 'Si',
    },
  },
  {
    id: 'codizvn',
    itemName: 'codizvn',
    dataType: 'flag',
    flagConfig: {
      '0': 'No',
      '1': 'Si',
    },
  },
  {
    id: 'datainizvalid',
    itemName: 'datainizvalid',
    dataType: 'dateUTC',
  },
  {
    id: 'datafinevali',
    itemName: 'datafinevali',
    dataType: 'dateUTC',
  },
];

export const PARTICELLA_COLUMN_CONFIG = [
  {
    id: 'comune',
    itemName: 'comune',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.comune',
  },
  {
    id: 'provincia',
    itemName: 'provincia',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.provincia',
  },
  {
    id: 'sezione',
    itemName: 'sezione',
    dataType: 'string',
  },
  {
    id: 'foglio',
    itemName: 'foglio',
    dataType: 'string',
  },
  {
    id: 'particella',
    itemName: 'particella',
    dataType: 'string',
  },
  {
    id: 'subalterno',
    itemName: 'subalterno',
    dataType: 'string',
  },
  {
    id: 'tipoConduzione',
    itemName: 'tipoConduzione',
    dataType: 'string',
  },
  {
    id: 'dataInizioConduzione',
    itemName: 'dataInizioConduzione',
    dataType: 'dateUTC',
  },
  {
    id: 'dataFineConduzione',
    itemName: 'dataFineConduzione',
    dataType: 'dateUTC',
  },
  {
    id: 'superficieCatastale',
    itemName: 'superficieCatastale',
    dataType: 'number',
    infopoint: 'dossier_table_infopoint.superficieCatastale',
  },
  {
    id: 'superficieCondotta',
    itemName: 'superficieCondotta',
    dataType: 'number',
    infopoint: 'dossier_table_infopoint.superficieCondotta',
  },
  {
    id: 'superficieGrafica',
    itemName: 'superficieGrafica',
    dataType: 'number',
  },
  {
    id: 'flagSuperficieIrrigua',
    itemName: 'flagirrigua',
    dataType: 'string',
  },
  {
    id: 'flagTerrazata',
    itemName: 'flagterrazzata',
    dataType: 'string',
  },
  {
    id: 'rotazioneCultureOrtive',
    itemName: 'rotazioneCultureOrtive',
    dataType: 'number',
  },
  {
    id: 'effluentiZootecnici',
    itemName: 'effluentiZootecnici',
    dataType: 'number',
  },
  {
    id: 'sostanzePericolose',
    itemName: 'sostanzePericolose',
    dataType: 'number',
  },
  {
    id: 'casiParticolari',
    itemName: 'casiParticolari',
    dataType: 'string',
  },
  {
    id: 'flagDocumentazioneGiustificativa',
    itemName: 'flagDocumentazioneGiustificativa',
    dataType: 'boolean',
  },
  {
    id: 'codiceZoneVulnerabili',
    itemName: 'codiceZoneVulnerabili',
    dataType: 'number',
  },
];

export const OWNERS_COLUMN_CONFIG = [
  {
    id: 'comune',
    itemName: 'comune',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.comune',
  },
  {
    id: 'provincia',
    itemName: 'provincia',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.provincia',
  },
  {
    id: 'sezione',
    itemName: 'sezione',
    dataType: 'string',
  },
  {
    id: 'foglio',
    itemName: 'foglio',
    dataType: 'string',
  },
  {
    id: 'particella',
    itemName: 'particella',
    dataType: 'string',
  },
  {
    id: 'subalterno',
    itemName: 'subalterno',
    dataType: 'string',
  },
  {
    id: 'tipoConduzione',
    itemName: 'tipoConduzione',
    dataType: 'flag',
    flagConfig: {
      '1': 'Proprietà o comproprietà',
      '2': 'Affitto',
      '3': 'Mezzadria',
      '4': 'Altra forma',
      '5': 'Est.Inf.5.000mq Comune Montano(DL.24/06/2014 N.91)',
    },
  },
  {
    id: 'dataInizioConduzione',
    itemName: 'dataInizioConduzione',
    dataType: 'dateUTC',
  },
  {
    id: 'dataFineConduzione',
    itemName: 'dataFineConduzione',
    dataType: 'dateUTC',
  },
  {
    id: 'cuaaprop',
    itemName: 'cuaaprop',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.cuaaprop',
  },
  {
    id: 'datainizvalid',
    itemName: 'datainizvalid',
    dataType: 'dateUTC',
  },
  {
    id: 'datafinevali',
    itemName: 'datafinevali',
    dataType: 'dateUTC',
  },
];

export const TERRITORY_COLUMN_CONFIG = [
  {
    id: 'comune',
    itemName: 'comune',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.comune',
  },
  {
    id: 'provincia',
    itemName: 'provincia',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.provincia',
  },
  {
    id: 'sezione',
    itemName: 'sezione',
    dataType: 'string',
  },
  {
    id: 'foglio',
    itemName: 'foglio',
    dataType: 'string',
  },
  {
    id: 'particella',
    itemName: 'particella',
    dataType: 'string',
  },
  {
    id: 'subalterno',
    itemName: 'subalterno',
    dataType: 'string',
  },
  {
    id: 'superficieCatastale',
    itemName: 'superficieCatastale',
    dataType: 'number',
    infopoint: 'dossier_table_infopoint.superficieCatastale',
  },
  {
    id: 'superficieCondotta',
    itemName: 'superficieCondotta',
    dataType: 'number',
    infopoint: 'dossier_table_infopoint.superficieCondotta',
  },
  {
    id: 'superficieGrafica',
    itemName: 'superficieGrafica',
    dataType: 'number',
  },
  {
    id: 'codiceOccupazioneSuolo',
    itemName: 'codiceOccupazioneSuolo',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.codiceOccupazioneSuolo',
  },
  {
    id: 'codiceDestinazioneUso',
    itemName: 'codiceDestinazioneUso',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.codiceDestinazioneUso',
  },
  {
    id: 'codiceUso',
    itemName: 'codiceUso',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.codiceUso',
  },
  {
    id: 'codiceQualita',
    itemName: 'codiceQualita',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.qualita',
  },
  {
    id: 'codiceOccupazioneVarieta',
    itemName: 'codiceOccupazioneVarieta',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.codiceOccupazioneVarieta',
  },
  {
    id: 'superficieUtilizzata',
    itemName: 'superficieUtilizzata',
    dataType: 'number',
    infopoint: 'dossier_table_infopoint.superficieUtilizzata',
  },
  {
    id: 'codiceMacroUso',
    itemName: 'codiceMacroUso',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.codiceMacroUso',
  },
  {
    id: 'codiceProdotto',
    itemName: 'codiceProdotto',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.codiceProdotto',
  },
  {
    id: 'codiceVarieta',
    itemName: 'codiceVarieta',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.codiceVarieta',
  },
  {
    id: 'superficieEleggibile',
    itemName: 'superficieEleggibile',
    dataType: 'number',
    infopoint: 'dossier_table_infopoint.superficieEleggibile',
  },
  {
    id: 'flagColtivoPrincipale',
    itemName: 'flagColtivoPrincipale',
    dataType: 'flag',
    flagConfig: {
      '0': 'Non è coltura principale (indica che non è varietà prevalente per olivo)',
      '1': 'coltura principale (indica varietà prevalente per olivo)',
    },
  },
  {
    id: 'dataInizioUtilizzo',
    itemName: 'dataInizioUtilizzo',
    dataType: 'dateUTC',
    infopoint: 'dossier_table_infopoint.dataInizioUtilizzo',
  },
  {
    id: 'dataFineUtilizzo',
    itemName: 'dataFineUtilizzo',
    dataType: 'dateUTC',
    infopoint: 'dossier_table_infopoint.dataFineUtilizzo',
  },
  {
    id: 'mantenimentoSuperficieAgricola',
    itemName: 'mantenimentoSuperficieAgricola',
    dataType: 'flag',
    flagConfig: {
      '1': 'Nessuna pratica',
      '2': 'Pratica ordinaria',
    },
  },
  {
    id: 'tipoCertificazione',
    itemName: 'tipoCertificazione',
    dataType: 'flag',
    infopoint: 'dossier_table_infopoint.tipoCertificazione',
    flagConfig: {
      '0': 'Nessuna',
      '1': 'DOP',
      '2': 'IGP',
    },
  },
  {
    id: 'flagProduzioneIntegrata',
    itemName: 'flagProduzioneIntegrata',
    dataType: 'flag',
    infopoint: 'dossier_table_infopoint.flagProduzioneIntegrata',
    flagConfig: {
      '0': 'No',
      '1': 'Si',
    },
  },
  {
    id: 'datafinevali',
    itemName: 'datafinevali',
    dataType: 'dateUTC',
  },
  {
    id: 'datainizvalid',
    itemName: 'datainizvalid',
    dataType: 'dateUTC',
  },
  {
    id: 'codiceLivello',
    itemName: 'codiceLivello',
    dataType: 'string',
  },
  {
    id: 'flagColtivazioneBio',
    itemName: 'flagColtivazioneBio',
    dataType: 'flag',
    flagConfig: {
      '0': 'Non biologico',
      '1': 'Biologico',
    },
  },
  {
    id: 'codiceTipoSemina',
    itemName: 'codiceTipoSemina',
    dataType: 'flag',
    flagConfig: {
      '1': 'Tradizionale',
      '2': 'Su sodo',
      '3': 'Minimum tillage',
      '4': 'Pratiche equivalenti',
    },
  },
  {
    id: 'codiceTipoIrrigazione',
    itemName: 'codiceTipoIrrigazione',
    dataType: 'flag',
    flagConfig: {
      '1': 'Sommersione',
      '2': 'Scorrimento',
      '3': 'Aspersione o a pioggia',
      '4': 'Microportate o a goccia',
      '5': 'Subirrigazione',
    },
  },
  {
    id: 'codiceProtezioneColture',
    itemName: 'codiceProtezioneColture',
    dataType: 'flag',
    flagConfig: {
      '1': 'Reti antigrandine',
      '2': 'Reti antiacqua',
      '3': 'Serre e tunnel fissi',
      '4': 'Ombrai',
      '5': 'Impianti antibrina',
    },
  },
  {
    id: 'codiceFaseAllevamento',
    itemName: 'codiceFaseAllevamento',
    dataType: 'flag',
    infopoint: 'dossier_table_infopoint.codiceFaseAllevamento',
    flagConfig: {
      '1': 'Produttivo',
      '2': 'Non produttivo',
    },
  },
  {
    id: 'codiceFormaAllevamento',
    itemName: 'codiceFormaAllevamento',
    dataType: 'string',
  },
  {
    id: 'mantenimentoPratiPermanenti',
    itemName: 'mantenimentoPratiPermanenti',
    dataType: 'flag',
    flagConfig: {
      '1': 'Pascolamento con animali propri',
      '2': 'Pascolamento con animali di terzi',
      '3': 'Sfalcio manuale',
      '4': 'Sfalcio meccanizzato',
      '5': 'Pratiche colturali volte al miglioramento',
      '6': 'Sfalcio con cadenza biennale',
      '7': 'Pascolamento e sfalcio',
      '8': 'Nessuna pratica',
      '10': "Pratica stabilita nell'ambito delle misure di conservazione o dei piani di gestione prescritti dagli enti dei siti di importanza comunitaria (SIC) e delle zone di protezione speciale (ZPS)",
    },
  },
  {
    id: 'superficieEleggibileOp',
    itemName: 'superficieEleggibileOp',
    dataType: 'number',
    infopoint: 'dossier_table_infopoint.superficieEleggibileOp',
  },
  {
    id: 'numeroPiante',
    itemName: 'numeroPiante',
    dataType: 'number',
    infopoint: 'dossier_table_infopoint.numeroPiante',
  },
  {
    id: 'sestoSuFila',
    itemName: 'sestoSuFila',
    dataType: 'number',
    infopoint: 'dossier_table_infopoint.sestoSuFila',
  },
  {
    id: 'sestoTrafile',
    itemName: 'sestoTrafile',
    dataType: 'number',
    infopoint: 'dossier_table_infopoint.sestoTrafile',
  },
  {
    id: 'annoImpianto',
    itemName: 'annoImpianto',
    dataType: 'string',
  },
  {
    id: 'tipoImpianto',
    itemName: 'tipoImpianto',
    dataType: 'flag',
    flagConfig: {
      '1': 'Regolare',
      '2': 'Irregolare',
    },
  },
  {
    id: 'percentuale',
    itemName: 'percentuale',
    dataType: 'number',
    infopoint: 'dossier_table_infopoint.percentuale',
  },
  {
    id: 'menzione',
    itemName: 'menzione',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.menzione',
  },
  {
    id: 'tipoUtilizzoOlivo',
    itemName: 'tipoUtilizzoOlivo',
    dataType: 'flag',
    flagConfig: {
      '1': 'Promiscuo',
      '2': 'Specializzato',
    },
  },
  {
    id: 'capacitaProduttiva',
    itemName: 'capacitaProduttiva',
    dataType: 'number',
    infopoint: 'dossier_table_infopoint.capacitaProduttiva',
  },
  {
    id: 'altitudine',
    itemName: 'altitudine',
    dataType: 'number',
    infopoint: 'dossier_table_infopoint.altitudine',
  },
];

export const ZOOTECHNIC_COLUMN_CONFIG = [
  {
    id: 'idAllevamentoBdn',
    itemName: 'idAllevamentoBdn',
    dataType: 'string',
  },
  {
    id: 'codiceazienda',
    itemName: 'codiceazienda',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.codiceazienda',
  },
  {
    id: 'specie',
    itemName: 'specie',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.specie',
  },
  {
    id: 'codiceFiscalePropietario',
    itemName: 'codiceFiscalePropietario',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.codiceFiscalePropietario',
  },
  {
    id: 'denominazionePropietario',
    itemName: 'denominazionePropietario',
    dataType: 'string',
  },
  {
    id: 'tipoProduzione',
    itemName: 'tipoProduzione',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.tipoProduzione',
  },
  {
    id: 'autorizzazioneLatte',
    itemName: 'autorizzazioneLatte',
    dataType: 'boolean',
  },
  {
    id: 'iscrizioneLibriGene',
    itemName: 'iscrizioneLibriGene',
    dataType: 'flag',
    flagConfig: {
      '0': 'Non iscritto',
      '1': 'Iscritto',
    },
  },
  {
    id: 'dataInizioAttivita',
    itemName: 'dataInizioAttivita',
    dataType: 'dateUTC',
    infopoint: 'dossier_table_infopoint.dataInizioAttivita',
  },
  {
    id: 'dataFineAttivita',
    itemName: 'dataFineAttivita',
    dataType: 'dateUTC',
    infopoint: 'dossier_table_infopoint.dataFineAttivita',
  },
  {
    id: 'destinazioneAllevamento',
    itemName: 'destinazioneAllevamento',
    dataType: 'flag',
    flagConfig: {
      '0': 'Non biologico',
      '1': 'Biologico',
      '2': 'In conversione',
    },
  },
  {
    id: 'dataInizioStalla',
    itemName: 'dataInizioStalla',
    dataType: 'dateUTC',
  },
  {
    id: 'dataFineStalla',
    itemName: 'dataFineStalla',
    dataType: 'dateUTC',
  },
  {
    id: 'indirizzo',
    itemName: 'indirizzo',
    dataType: 'string',
  },
  {
    id: 'localita',
    itemName: 'localita',
    dataType: 'string',
  },
  {
    id: 'cap',
    itemName: 'cap',
    dataType: 'string',
  },
  {
    id: 'provincia',
    itemName: 'provincia',
    dataType: 'string',
  },
  {
    id: 'comune',
    itemName: 'comune',
    dataType: 'string',
  },
  {
    id: 'comuneStalla',
    itemName: 'comuneStalla',
    dataType: 'string',
  },
  {
    id: 'codiceFiscale',
    itemName: 'codiceFiscale',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.codfiscale',
  },
  {
    id: 'flagSoccidario',
    itemName: 'flagSoccidario',
    dataType: 'flag',
    flagConfig: {
      '0': 'Non è soccidario',
      '1': "E' soccidario",
    },
  },
  {
    id: 'codiceFiscaleDetentore',
    itemName: 'codiceFiscaleDetentore',
    dataType: 'string',
    infopoint: 'dossier_table_infopoint.codfiscale',
  },
  {
    id: 'flagSoccidarioDetentore',
    itemName: 'flagSoccidarioDetentore',
    dataType: 'flag',
    flagConfig: {
      '0': 'Non è soccidario',
      '1': "E' soccidario",
    },
  },
  {
    id: 'numeroCapiLatte',
    itemName: 'numeroCapiLatte',
    dataType: 'number',
  },
  {
    id: 'decoRazzaPrevalente',
    itemName: 'decoRazzaPrevalente',
    dataType: 'string',
  },
  {
    id: 'numeroCapi6a24Mesi',
    itemName: 'numeroCapi6a24Mesi',
    dataType: 'number',
  },
  {
    id: 'numeroCapiOltre24Mesi',
    itemName: 'numeroCapiOltre24Mesi',
    dataType: 'number',
  },
  {
    id: 'numeroVaccheLatte',
    itemName: 'numeroVaccheLatte',
    dataType: 'number',
  },
  {
    id: 'numeroBufali',
    itemName: 'numeroBufali',
    dataType: 'number',
  },
  {
    id: 'dataInizio',
    itemName: 'dataInizio',
    dataType: 'dateUTC',
  },
  {
    id: 'dataFine',
    itemName: 'dataFine',
    dataType: 'dateUTC',
  },
  {
    id: 'numeroCapiTotale',
    itemName: 'numeroCapiTotale',
    dataType: 'number',
  },
  {
    id: 'numeroVitelliFino6Mesi',
    itemName: 'numeroVitelliFino6Mesi',
    dataType: 'number',
  },
  {
    id: 'numeroVitelli6a24Mesi',
    itemName: 'numeroVitelli6a24Mesi',
    dataType: 'number',
  },
  {
    id: 'numeroFemmineMacello6a24Mesi',
    itemName: 'numeroFemmineMacello6a24Mesi',
    dataType: 'number',
  },
  {
    id: 'numeroCapiAllevamento6a24Mesi',
    itemName: 'numeroCapiAllevamento6a24Mesi',
    dataType: 'number',
  },
  {
    id: 'numeroCapiAllevamentoFemmine6a24Mesi',
    itemName: 'numeroCapiAllevamentoFemmine6a24Mesi',
    dataType: 'number',
  },
  {
    id: 'numeroCapiMacelloOltre24Mesi',
    itemName: 'numeroCapiMacelloOltre24Mesi',
    dataType: 'number',
  },
  {
    id: 'numeroCapiMacelloFemmineOltre24Mesi',
    itemName: 'numeroCapiMacelloFemmineOltre24Mesi',
    dataType: 'number',
  },
  {
    id: 'numeroCapiAllevamentoOltre24Mesi',
    itemName: 'numeroCapiAllevamentoOltre24Mesi',
    dataType: 'number',
  },
  {
    id: 'numeroCapiAllevamentoFemmineOltre24Mesi',
    itemName: 'numeroCapiAllevamentoFemmineOltre24Mesi',
    dataType: 'number',
  },
  {
    id: 'numeroCapiAllevamentoFinoA6Mesi',
    itemName: 'numeroCapiAllevamentoFinoA6Mesi',
    dataType: 'number',
  },
  {
    id: 'numeroCapiAllevamentoOltre6Mesi',
    itemName: 'numeroCapiAllevamentoOltre6Mesi',
    dataType: 'number',
  },
  {
    id: 'numeroOviniCapriniOltre12Mesi',
    itemName: 'numeroOviniCapriniOltre12Mesi',
    dataType: 'number',
  },
  {
    id: 'numeroOviniCapriniFemmineOltre12Mesi',
    itemName: 'numeroOviniCapriniFemmineOltre12Mesi',
    dataType: 'number',
  },
  {
    id: 'numeroOviniCapriniRimonta',
    itemName: 'numeroOviniCapriniRimonta',
    dataType: 'number',
  },
  {
    id: 'numeroOviniCapriniFemmineRimonta',
    itemName: 'numeroOviniCapriniFemmineRimonta',
    dataType: 'number',
  },
  {
    id: 'numeroTori',
    itemName: 'numeroTori',
    dataType: 'number',
  },
  {
    id: 'numeroAltreVacche',
    itemName: 'numeroAltreVacche',
    dataType: 'number',
  },
  {
    id: 'numeroAltreVaccheDichiarate',
    itemName: 'numeroAltreVaccheDichiarate',
    dataType: 'number',
  },
  {
    id: 'metodoAllevamento',
    itemName: 'metodoAllevamento',
    dataType: 'flag',
    flagConfig: {
      '1': 'Estensivo',
      '2': 'Semiestensivo',
      '3': 'Intensivo',
    },
  },
  {
    id: 'datainizvalid',
    itemName: 'datainizvalid',
    dataType: 'dateUTC',
  },
  {
    id: 'datafinevali',
    itemName: 'datafinevali',
    dataType: 'dateUTC',
  },
];
